//#region IMPORTS
import {
  instoreThemesSelectors,
  clearInstoreThemeParams,
  deleteInstoreThemeAsync,
} from "../../features/instoreThemes/instoreThemesSlice";
import {
  DELETE_INSTORE_THEME_SURE,
  CREATE_OR_UPDATE_INSTORE_THEMES_MODAL,
} from "../../models/_consts";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { InstoreTheme } from "../../models/instoreTheme";
import { openModal } from "../../features/dashBoard/dashboardSlice";
import { toast } from "react-toastify";
import InstoreOrdersSettingsDropdown from "../instoreOrders/InstoreOrdersSettingsDropdown";
import InstoreThemesFilterBar from "./InstoreThemesFilterBar";
import InstoreThemesTable from "./InstoreThemesTable";
import InstoreThemeForm from "./InstoreThemeForm";
import useAdminManager from "../../hooks/useAdminManager";
import MainContainer from "../_layout/MainContainer";
import useHQmanager from "../../hooks/useHQmanager";
import AreYouSure from "../modalTypes/AreYouSure";
import PageHeader from "../genericPageHeader/PageHeader";
import MainModal from "../_layout/MainModal";
//#endregion

//#region INTERFACE
interface Props {
  title?: string;
}
//#endregion

export default function InstoreThemesContainer({
  title = "rituals.instoreThemes",
}: Props) {
  //#region SETUP
  // Translation
  const { t } = useTranslation();

  // Redux
  const dispatch = useAppDispatch();
  const instoreThemes = useAppSelector(instoreThemesSelectors.selectAll);
  const { loading, instoreThemes_Loaded, metaData, status } = useAppSelector(
    ({ instoreThemes }) => instoreThemes,
  );
  const { user } = useAppSelector(({ account }) => account);

  // Local state
  const [savingDataUpload] = useState<boolean>(false);
  const [selectedTheme, setSelectedTheme] = useState<InstoreTheme>();

  // Roles
  const isAdmin = useAdminManager(user);
  const hqTeam = useHQmanager(user);
  //#endregion

  //#region LOGIC
  // Create or update theme
  const createOrUpdateThemeHandler = (instoreTheme?: InstoreTheme) => {
    setSelectedTheme(instoreTheme);
    dispatch(openModal(CREATE_OR_UPDATE_INSTORE_THEMES_MODAL));
  };

  // Delete theme warning
  const deleteThemeWarningHandler = (instoreTheme: InstoreTheme) => {
    setSelectedTheme(instoreTheme);
    dispatch(openModal(DELETE_INSTORE_THEME_SURE));
  };

  // Delete theme confirm
  const deleteThemeHandler = async () => {
    if (selectedTheme) {
      await dispatch(deleteInstoreThemeAsync({ id: selectedTheme.id }));
      dispatch(openModal(false));
    }
  };

  // Cancel create or update promo
  const cancelCRUDTheme = () => {
    if (!savingDataUpload) {
      setSelectedTheme(undefined);
      dispatch(openModal(false));
    } else {
      toast.error("Some background tasks are stil running. Please wait...");
    }
  };

  // Reset handler
  const resetHandler = () => {
    setSelectedTheme(undefined);
    dispatch(clearInstoreThemeParams());
  };
  //#endregion

  //#region RENDER
  return (
    <Fragment>
      {/* MODALS */}
      <MainModal
        type={DELETE_INSTORE_THEME_SURE}
        closeHandler={cancelCRUDTheme}
      >
        <AreYouSure
          confirmHandler={deleteThemeHandler}
          closeHandler={cancelCRUDTheme}
          message={`Are you sure you want to delete theme ${selectedTheme?.name}?`}
          title="Delete theme?"
        />
      </MainModal>

      <MainModal
        type={CREATE_OR_UPDATE_INSTORE_THEMES_MODAL}
        closeHandler={cancelCRUDTheme}
      >
        <InstoreThemeForm
          instoreTheme={selectedTheme}
          cancelCUD={cancelCRUDTheme}
          disabled={loading && status.includes("PendingCreateInstoreTheme")}
        />
      </MainModal>

      {/* BODY */}
      <MainContainer>
        <PageHeader
          createClickHandler={() => createOrUpdateThemeHandler(undefined)}
          createButtonValue={t("rituals.addTheme")}
          extraHQButtons={
            <InstoreOrdersSettingsDropdown
              hide={!isAdmin}
              selectedLink="themes"
            />
          }
          disabled={loading}
          isAdmin={isAdmin}
          hqTeam={hqTeam}
          hqCanAdd
          title={t(title)}
          reset={resetHandler}
        />

        <InstoreThemesFilterBar />

        <InstoreThemesTable
          editHandler={(item: InstoreTheme) => createOrUpdateThemeHandler(item)}
          deleteHandler={(item: InstoreTheme) =>
            deleteThemeWarningHandler(item)
          }
          itemsLoaded={instoreThemes_Loaded}
          metaData={metaData}
          loading={loading}
          items={instoreThemes}
          hide={instoreThemes_Loaded && instoreThemes.length <= 0}
        />
      </MainContainer>
    </Fragment>
  );
  //#endregion
}
